import React, { useEffect } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { SettingsList } from "./SettingsList";
import { Box, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/appHooks";
import { MyProfile } from "./MyProfile";
import {
  CommunityTypes,
  RoleTypes,
  SettingListKeys,
} from "../../helpers/constants";
import { CommunityInfo } from "./CommunityInfo";
import { EditCommunityUser } from "./EditCommunityUser";
import { EditNotificationGroup } from "./EditNotificationGroup";
import { NotificationSettings } from "./NotificationSettings";
import { AddCommunityUser } from "./AddCommunityUser";
import {
  getCommunityUnits,
  loadCommunityGroups,
  loadCommunityUsers,
} from "../header/headerThunks";
import { clearOrganizationSettings, setSettingsUnit } from "./settingsSlice";
import { getEmergencyContacts, getResidentInfo } from "./settingsThunks";
import { getResidentContacts } from "../common/commonSlice";
import { HandOff } from "./HandOff";
import {
  loadHandoffSettings,
  loadNotificationSettings,
} from "../app/asyncThunkActions";
import { FloatingNotificationPanel } from "../common/FloatingNotificationPanel";
import { AddUnitForm } from "./AddUnitForm";
import { unitOptions } from "./ResidentUnitsList";
import { EditCommunityUnit } from "./EditCommunityUnit";
import { ResidentSettingsForm } from "./ResidentSettingsForm";
import { ResidentContactsSettings } from "./ResidentContactsSettings";
import { EmergencyContacts } from "./EmergencyContacts";
import { NotificationGroupConfig } from "./NotificationGroupConfig";
import { loadUnitGroupsByEventType } from "./settingsThunks";
import { PauseStackCare } from "./PauseStackCare";
import { minToolbarHeight } from "../app/App";
import { RFIDSettingsForm } from "./RFIDSettingsForm";
import { AipNotificationSettings } from "./AipNotificationSettings";
import { SSOConfiguration } from "./SSOConfiguration";
import { loadOrganizationSettings } from "./settingsThunks";
import { GroupMapping } from "./GroupMapping";

const useStyles = <T extends { containerLayout: string; showBanner: boolean }>(
  props: T
) =>
  makeStyles((theme: Theme) =>
    createStyles({
      settingsContainer: {
        display: "flex",
        height: `calc(100vh - ${minToolbarHeight(props.showBanner, theme)})`,
      },
      settingsNavPanel: {
        display: "flex",
        width: props.containerLayout,
      },
      settingsContent: {
        display: "flex",
        flex: "1",
        justifyContent: "center",
        height: "100%",
      },
    })
  );

const EmptySectionIndicator = (props: { text: string }) => {
  return (
    <Box
      height="100%"
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="100%"
      whiteSpace="pre-line"
      textAlign="center"
    >
      <Typography variant="subtitle2">{props.text}</Typography>
    </Box>
  );
};

const SettingsOptions = (props: {
  selectedOption?: string;
  selectedUser?: string;
  userCount: number;
  selectedGroup?: string;
  selectedGroupConfig: boolean;
  selectedEventType?: string;
  selectedUnitOption?: string;
  addCommunityUser: boolean;
  addNotificationGroup: boolean;
  addUnit: boolean;
  selectedUnit?: string;
  selectedRFIDConfig?: boolean;
  selectedEntraConfigOpt?: string;
  selectedOrganizationID?: string;
  hasSSOConfiguration?: boolean;
}) => {
  const {
    selectedOption,
    selectedUser,
    userCount,
    selectedGroup,
    selectedGroupConfig,
    selectedEventType,
    selectedUnitOption,
    addCommunityUser,
    addNotificationGroup,
    addUnit,
    selectedRFIDConfig,
    selectedEntraConfigOpt,
    selectedOrganizationID,
    hasSSOConfiguration,
  } = props;
  const { t } = useTranslation();

  const pickSettingsComponent = () => {
    switch (selectedOption) {
      case "my_profile":
        return <MyProfile />;
      case SettingListKeys.community_info:
        return <CommunityInfo />;
      case SettingListKeys.community_users:
        if (addCommunityUser) {
          return <AddCommunityUser />;
        }
        if (selectedUser !== undefined) {
          return <EditCommunityUser />;
        }
        return (
          <EmptySectionIndicator
            text={userCount === 0 ? t("add_user") : t("select_user")}
          />
        );
      case SettingListKeys.hand_off:
        return <HandOff />;
      case SettingListKeys.notification_groups:
        if (selectedGroupConfig) {
          return <NotificationGroupConfig />;
        }
        if (selectedGroup !== undefined || addNotificationGroup) {
          return <EditNotificationGroup />;
        }
        return <EmptySectionIndicator text={t("select_group")} />;

      case SettingListKeys.notification_settings:
        if (selectedEventType !== undefined) {
          return <NotificationSettings />;
        }
        return <EmptySectionIndicator text={t("select_notification")} />;
      case SettingListKeys.rfid_tags:
        if (selectedRFIDConfig === true) {
          return <RFIDSettingsForm />;
        } else {
          return <EmptySectionIndicator text={t("select_setting")} />;
        }
      case SettingListKeys.microsoft_entra:
        if (
          selectedOrganizationID !== undefined &&
          selectedEntraConfigOpt === SettingListKeys.entra_sso_configuration
        ) {
          return <SSOConfiguration />;
        }
        if (
          selectedOrganizationID !== undefined &&
          hasSSOConfiguration &&
          selectedEntraConfigOpt === SettingListKeys.entra_group_mapping
        ) {
          return <GroupMapping />;
        }
        return <EmptySectionIndicator text={t("select_setting")} />;
      case SettingListKeys.aip_resident:
        return <ResidentSettingsForm />;
      case SettingListKeys.aip_pause_stackcare:
        return <PauseStackCare />;
      case SettingListKeys.aip_my_notifications:
        if (selectedEventType !== undefined) {
          return <AipNotificationSettings />;
        }
        return <EmptySectionIndicator text={t("select_notification")} />;
      case SettingListKeys.aip_emergency_contacts:
        return <EmergencyContacts />;
      case SettingListKeys.units_residents:
        // If unit has no resident, an add resident option will be shown
        // If unit has a resident, regular options will be shown
        if (addUnit) {
          return <AddUnitForm />;
        }
        if (
          selectedUnitOption === unitOptions.resident_settings ||
          selectedUnitOption === unitOptions.add_resident
        ) {
          return <ResidentSettingsForm />;
        }

        if (selectedUnitOption === "resident_contacts") {
          return <ResidentContactsSettings />;
        }
        if (selectedUnitOption === "unit_settings") {
          return <EditCommunityUnit />;
        }
        if (selectedUnitOption === "pause_stackcare") {
          return <PauseStackCare />;
        }
        if (selectedUnitOption === "emergency_contacts") {
          return <EmergencyContacts />;
        }

        return <EmptySectionIndicator text={t("select_unit_resident")} />;
      case SettingListKeys.organization_info:
      case SettingListKeys.organization_users:
      case SettingListKeys.organization_groups:
      default:
        return <EmptySectionIndicator text={t("select_setting")} />;
    }
  };

  return pickSettingsComponent();
};

export default function Settings() {
  /* ---- Hooks ---- */
  const dispatch = useAppDispatch();
  const communityID = useAppSelector(
    (state) => state.headerState.selectedCommunity?.id
  );

  const selectedOption = useAppSelector(
    (state) => state.settingsState.selectedOption
  );
  const selectedUser = useAppSelector(
    (state) => state.settingsState.selectedUser
  );
  const userCount = useAppSelector(
    (state) => state.headerState.users?.length ?? 0
  );
  const selectedGroup = useAppSelector(
    (state) => state.settingsState.selectedGroup
  );
  const selectedGroupConfig = useAppSelector(
    (state) => state.settingsState.selectedGroupConfig
  );
  const selectedEventType = useAppSelector(
    (state) => state.settingsState.selectedEventType
  );
  const selectedUnit = useAppSelector(
    (state) => state.settingsState.selectedUnit
  );
  const addCommunityUser = useAppSelector(
    (state) => state.settingsState.addUser
  );
  const addNotificationGroup = useAppSelector(
    (state) => state.settingsState.addNotificationGroup
  );
  const addUnit = useAppSelector((state) => state.settingsState.addUnit);

  const selectedUnitOption = useAppSelector(
    (state) => state.settingsState.selectedUnitConfigOpt
  );

  const selectedRFIDConfig = useAppSelector(
    (state) => state.settingsState.selectedRFIDConfig
  );
  const selectedEntraConfigOpt = useAppSelector(
    (state) => state.settingsState.selectedEntraConfigOpt
  );
  const selectedOrganizationID = useAppSelector(
    (state) => state.headerState.selectedOrganization
  );
  const organizationSettings = useAppSelector(
    (state) => state.settingsState.organizationSettings
  );
  const installerRole = useAppSelector(
    (state) => state.headerState.installerRole
  );
  const isCaregiver = useAppSelector(
    (state) =>
      !!state.headerState.selectedCommunity?.role.includes(RoleTypes.caregiver)
  );

  const allNotifications = useAppSelector(
    (state) => state.headerState.allNotifications
  );

  const showBanner = useAppSelector((state) => state.headerState.showBanner);

  const isAipCommunity = useAppSelector(
    (state) =>
      state.headerState.selectedCommunity?.community_type === CommunityTypes.aip
  );
  const units = useAppSelector((state) => state.headerState.units);

  const getListContainerWidth = () => {
    switch (selectedOption) {
      case SettingListKeys.community_users:
      case SettingListKeys.notification_groups:
      case SettingListKeys.notification_settings:
      case SettingListKeys.rfid_tags:
      case SettingListKeys.aip_my_notifications:
        return "660px";
      case SettingListKeys.microsoft_entra:
        return "560px";
      case SettingListKeys.units_residents:
        if (selectedUnit) return "900px";
        return "600px";
      default:
        return "300px";
    }
  };

  const containerLayout = getListContainerWidth();
  const classes = useStyles({
    containerLayout: containerLayout,
    showBanner: showBanner,
  })();

  //Effects
  useEffect(() => {
    if (
      (selectedOption === SettingListKeys.community_users ||
        selectedOption === SettingListKeys.aip_emergency_contacts) &&
      communityID !== undefined
    ) {
      dispatch(loadCommunityUsers(communityID));
      if (!installerRole) {
        dispatch(loadCommunityGroups(communityID));
      }
    }
    if (
      selectedOption === SettingListKeys.notification_groups &&
      communityID !== undefined
    ) {
      dispatch(loadCommunityGroups(communityID));
      if (!isCaregiver) {
        dispatch(loadNotificationSettings({ community_id: communityID }));
        dispatch(loadUnitGroupsByEventType(communityID));
      }
    }
    if (
      selectedOption === SettingListKeys.hand_off &&
      communityID !== undefined
    ) {
      dispatch(loadHandoffSettings(communityID));
    }
    if (
      (selectedOption === SettingListKeys.notification_settings ||
        selectedOption === SettingListKeys.aip_my_notifications) &&
      communityID !== undefined
    ) {
      dispatch(
        loadNotificationSettings({
          community_id: communityID,
          useUnitConfigs:
            selectedOption === SettingListKeys.aip_my_notifications,
        })
      );
    }
    if (
      (selectedOption === SettingListKeys.units_residents ||
        selectedOption === SettingListKeys.rfid_tags ||
        selectedOption === SettingListKeys.aip_resident ||
        selectedOption === SettingListKeys.aip_pause_stackcare ||
        selectedOption === SettingListKeys.aip_emergency_contacts ||
        selectedOption === SettingListKeys.aip_my_notifications) &&
      communityID !== undefined
    ) {
      dispatch(getCommunityUnits(communityID));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption, communityID]);

  useEffect(() => {
    if (selectedOrganizationID === undefined) {
      dispatch(clearOrganizationSettings());
      return;
    }

    if (
      selectedOption === SettingListKeys.microsoft_entra &&
      organizationSettings?.id !== selectedOrganizationID
    ) {
      dispatch(loadOrganizationSettings(selectedOrganizationID));
    }
  }, [
    dispatch,
    organizationSettings?.id,
    selectedOption,
    selectedEntraConfigOpt,
    selectedOrganizationID,
  ]);

  // Auto select unit for AIP settings
  useEffect(() => {
    if (
      isAipCommunity &&
      (selectedOption === SettingListKeys.aip_resident ||
        selectedOption === SettingListKeys.aip_pause_stackcare ||
        selectedOption === SettingListKeys.aip_emergency_contacts ||
        selectedOption === SettingListKeys.aip_my_notifications) &&
      units &&
      units.length > 0 &&
      selectedUnit === undefined
    ) {
      const unitId = units[0].id;
      dispatch(setSettingsUnit(unitId));
      const resident = units[0].residents?.[0];
      if (resident && communityID) {
        dispatch(
          getResidentInfo({
            community_id: communityID,
            resident_id: resident.id,
          })
        );
        if (selectedOption === SettingListKeys.aip_emergency_contacts) {
          dispatch(getEmergencyContacts(resident.id));
          dispatch(
            getResidentContacts({
              community_id: communityID,
              resident_id: resident.id,
            })
          );
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAipCommunity, selectedOption, units, selectedUnit]);

  // Load emergency contacts for AIP
  useEffect(() => {
    if (
      isAipCommunity &&
      selectedOption === SettingListKeys.aip_emergency_contacts &&
      units &&
      units.length > 0 &&
      selectedUnit !== undefined &&
      communityID
    ) {
      const resident = units[0].residents?.[0];
      if (resident) {
        dispatch(getEmergencyContacts(resident.id));
        dispatch(
          getResidentContacts({
            community_id: communityID,
            resident_id: resident.id,
          })
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption, selectedUnit]);

  return (
    <div className={classes.settingsContainer}>
      <div className={classes.settingsNavPanel}>
        <SettingsList />
      </div>
      <div className={classes.settingsContent}>
        <SettingsOptions
          selectedOption={selectedOption}
          selectedUser={selectedUser}
          userCount={userCount}
          selectedGroup={selectedGroup}
          selectedGroupConfig={selectedGroupConfig}
          selectedEventType={selectedEventType}
          selectedUnitOption={selectedUnitOption}
          addCommunityUser={addCommunityUser}
          addNotificationGroup={addNotificationGroup}
          addUnit={addUnit}
          selectedUnit={selectedUnit}
          selectedRFIDConfig={selectedRFIDConfig}
          selectedEntraConfigOpt={selectedEntraConfigOpt}
          selectedOrganizationID={selectedOrganizationID}
          hasSSOConfiguration={!!organizationSettings?.sso_config}
        />
      </div>
      {!installerRole && !allNotifications && <FloatingNotificationPanel />}
    </div>
  );
}
